import React, {ReactNode} from 'react';

import {withRouter, WithRouterProps} from 'react-router';

import {
  applyFilter,
  DashboardFilterbar,
} from 'app/general/dashboards/DashboardFilterbar';
import {DashboardFilterbarSort} from 'app/general/dashboards/DashboardFilterbarSort';
import {DashboardFilterbarState} from 'app/general/dashboards/DashboardFilterbarStateFilter';
import {DashboardFilterbarTags} from 'app/general/dashboards/DashboardFilterbarTags';
import {DashboardFilterbarTeams} from 'app/general/dashboards/DashboardFilterbarTeams';
import {SearchInputField} from 'app/general/widgets/SearchInputField';
import {SORT_BY_TYPES, SortOptionType} from 'app/utils/constants';

import type {ContentType} from 'sweet-types';

type OwnProps = {
  ct: ContentType;
  viewingAll: boolean;
  resetFilters: () => void;
  hideFilters?: Array<string>;
  show: {
    team: boolean;
    search: boolean;
    tags: boolean;
    filters: boolean;
    sort: boolean;
  };
  children?: ReactNode;
  className?: string;
  borderless?: boolean;
  searchFieldClass?: string;
  tagsFieldClass?: string;
  sortOptions?: Array<string>;
  backendDefaultSort?: SortOptionType;
};

type Props = WithRouterProps & OwnProps;

const SORT_OPTIONS = [
  SORT_BY_TYPES.MODIFICATION_DATE,
  SORT_BY_TYPES.APPROVED_DATE_DESC,
  SORT_BY_TYPES.REVIEWED_DATE_DESC,
  SORT_BY_TYPES.REVIEWED_DATE_ASC,
  SORT_BY_TYPES.CREATION_DATE,
  SORT_BY_TYPES.ALPHABETHICALLY,
  SORT_BY_TYPES.RANK,
  SORT_BY_TYPES.REVIEWED_DATE_NEXT_ASC,
];

export const Filterbar = withRouter<Props>(
  ({
    location,
    router,
    resetFilters,
    viewingAll,
    className,
    borderless,
    show,
    searchFieldClass,
    tagsFieldClass,
    ct,
    children,
    hideFilters,
    backendDefaultSort,
    sortOptions = SORT_OPTIONS,
  }: Props) => {
    const getSortOptions = () => {
      return location.query.search == null
        ? sortOptions.filter((i) => i !== SORT_BY_TYPES.RANK)
        : sortOptions;
    };

    const onSearch = (search) => {
      changeFilter({
        search,
        ordering: search !== '' ? SORT_BY_TYPES.RANK : undefined,
      });
    };

    const onSort = (ordering) => {
      changeFilter({
        ordering,
      });
    };

    const onFilterChange = (filters: Array<string>) => {
      changeFilter({
        filter: filters.join(','),
      });
    };

    const onTeamChange = (team_id) => {
      changeFilter({
        team_id,
      });
      return Promise.resolve(true);
    };

    const onTagChange = (tag) => {
      changeFilter({
        tag,
      });
    };

    const changeFilter = (
      filters: Record<string, number | string | null | void>
    ) => {
      return applyFilter(router, location, filters);
    };

    return (
      <DashboardFilterbar
        resetFilters={resetFilters}
        viewingAll={viewingAll}
        className={className}
        borderless={borderless}
      >
        {show.search === true ? (
          <div
            className={
              searchFieldClass ??
              'col-12 col-sm-6 col-lg-4 col-xl-3 pr-sm-2 mb-2 mb-sm-2 mb-xl-0'
            }
          >
            <SearchInputField
              value={location.query.search != null ? location.query.search : ''}
              onChange={onSearch}
            />
          </div>
        ) : null}

        {show.sort === true ? (
          <div className="col-12 col-sm-6 col-lg-4 col-xl pr-md-0 pr-lg-2 mb-2 mb-sm-2 mb-xl-0">
            <DashboardFilterbarSort
              value={location.query.ordering}
              onChange={onSort}
              options={getSortOptions()}
              backendDefaultSort={backendDefaultSort}
            />
          </div>
        ) : null}

        {show.tags === true ? (
          <div
            className={
              tagsFieldClass ??
              'col-12 col-sm-6 col-lg-4 col-xl pr-sm-2 pr-lg-0 pr-xl-2 mb-2 mb-md-2 mb-xl-0'
            }
          >
            <DashboardFilterbarTags
              value={location.query.tag}
              onChange={onTagChange}
            />
          </div>
        ) : null}

        {show.team === true ? (
          <div className="col-12 col-sm-6 col-lg-6 col-xl pr-md-0 pr-lg-2 mb-2 mb-sm-2 mb-md-2 mb-lg-0">
            <DashboardFilterbarTeams
              value={location.query.team_id}
              onChange={onTeamChange}
            />
          </div>
        ) : null}

        {show.filters === true ? (
          <div className="col-12 col-sm col-lg col-xl-2 mb-md-0 d-flex justify-content-center">
            <DashboardFilterbarState
              ct={ct}
              hideFilters={hideFilters ?? ['']}
              filters={
                location.query.filter != null
                  ? location.query.filter.split(',')
                  : []
              }
              onChange={onFilterChange}
            />
          </div>
        ) : null}
        {children}
      </DashboardFilterbar>
    );
  }
);
